import { useTranslation } from 'react-i18next'
import { localizedName } from '../utils/localizedName'
import SwapWithSpareModal from '../components/repair/SwapWithSpareModal'
import { useAuth } from '../hooks/useAuth'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box, Text, Button, Badge, Group, Modal, Stack, Select,
  TextInput, Textarea, ActionIcon, Tooltip, Center, Loader,
  SimpleGrid, Paper, Alert, FileButton, Image, Grid, NumberInput,
  Anchor, Divider, Menu, Card,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconTool, IconX, IconCheck, IconAlertTriangle,
  IconArrowsExchange, IconFileText, IconFileCheck, IconSearch, IconUpload, IconEdit, IconVideo,
  IconRotateClockwise2, IconDotsVertical,
} from '@tabler/icons-react'
import SmartDateInput from '../components/SmartDateInput'
import AssetQuickView from '../components/assets/AssetQuickView'
import {
  getRepairAssets, getVendors,
  getRepairJobs, createRepairJob, completeRepair, downloadRepairPdf,
  replaceAsset, writeOffRepair, uploadRepairImages, deleteRepairImage, uploadRepairVideo, updateRepairJob, swapWithSpare, updateAsset,
  getInventoryItems, getAllAssetsAdmin, adjustStock, scanLookupAsset,
} from '../api/client'
import jsPDF from 'jspdf'
import { urlToBase64, drawHeader, registerThaiFont, PDF_FONT, getImageNaturalSize, fitFontSize , openPdfInNewTab} from '../assets/pdfHelper'

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'orange', COMPLETED: 'teal', REPLACED: 'violet', WRITTEN_OFF: 'red',
}
// STATUS_LABEL ย้ายเข้าไปเป็นฟังก์ชันในคอมโพเนนต์แทน (getStatusLabel) — เดิมเป็น const ระดับโมดูล
// ซึ่งอยู่นอก component เลยเรียก t() ไม่ได้ ทำให้ label เป็นภาษาอังกฤษตายตัวไม่ว่าจะสลับภาษาไหนก็ตาม

async function generateAssetWriteOffPDF(assets: any[], _logoB64?: string | null, _companyName?: string | null) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
  registerThaiFont(pdf)
  const W = 210, M = 15
  const today = new Date().toLocaleDateString('en-GB')
  const ref = `WO-${Date.now().toString().slice(-6)}`

  // Header — auto-fit โลโก้ + หัวข้อ ไม่บิดเบี้ยว/ไม่ตกกรอบ
  let y = await drawHeader(pdf, 'ASSET WRITE-OFF REPORT', '(Equipment Disposal / Decommission)', ref, today, _logoB64, _companyName)
  y -= 1

  // Section header helper
  const sectionHeader = (title: string) => {
    pdf.setFillColor(226, 232, 240); pdf.rect(M, y, W - M * 2, 7, 'F')
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9); pdf.setTextColor(30, 41, 59)
    pdf.text(title, M + 3, y + 5); pdf.setTextColor(0, 0, 0); y += 10
  }

  // Equipment list table
  sectionHeader(`Written-Off Equipment List (${assets.length} item${assets.length > 1 ? 's' : ''})`)

  // Table header
  const cols = { no: M, code: M + 8, name: M + 48, sn: M + 100, cat: M + 145, dept: M + 168 }
  pdf.setFillColor(51, 65, 85); pdf.rect(M, y, W - M * 2, 7, 'F')
  pdf.setTextColor(255, 255, 255); pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(7.5)
  pdf.text('No.', cols.no + 1, y + 5)
  pdf.text('Asset Code', cols.code, y + 5)
  pdf.text('Asset Name', cols.name, y + 5)
  pdf.text('Serial No.', cols.sn, y + 5)
  pdf.text('Category', cols.cat, y + 5)
  pdf.text('Dept', cols.dept, y + 5)
  pdf.setTextColor(0, 0, 0); y += 9

  assets.forEach((a, i) => {
    if (y > 250) { pdf.addPage(); y = M }
    const rowBg = i % 2 === 0
    if (rowBg) { pdf.setFillColor(248, 250, 252); pdf.rect(M, y - 3, W - M * 2, 7, 'F') }
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5)
    pdf.text(String(i + 1), cols.no + 1, y + 2)
    pdf.text(a.assetCode ?? '-', cols.code, y + 2)
    const name = a.name?.length > 20 ? a.name.slice(0, 19) + '…' : (a.name ?? '-')
    pdf.text(name, cols.name, y + 2)
    pdf.text((a.serialNumber ?? '-').slice(0, 18), cols.sn, y + 2)
    pdf.text((a.category ?? '-').slice(0, 12), cols.cat, y + 2)
    pdf.text((a.departmentCode ?? '-').slice(0, 10), cols.dept, y + 2)
    y += 7
  })

  y += 8

  // Reason section — แสดงข้อมูลจริงจาก asset (damagedNote + writeOffReason ถ้ามี) แทนเว้นว่างให้เขียนมือ
  // หมายเหตุ: รายงานนี้ใช้สำหรับขออนุมัติตัดจำหน่าย (asset ยังคงสถานะ DAMAGED รอดำเนินการ)
  // จึงอาจยังไม่มี writeOffReason จนกว่าจะผ่านขั้นตอน Write Off จริงในหน้า Disposal
  assets.forEach((a, i) => {
    if (y > 230) { pdf.addPage(); y = M }
    sectionHeader(`${i + 1}. ${a.assetCode} — Damage Detail`)
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8)

    pdf.setFont(PDF_FONT, 'bold'); pdf.text('Damage Reason:', M + 3, y); pdf.setFont(PDF_FONT, 'normal')
    const dmgLines = pdf.splitTextToSize(
      [a.damagedDate ? `(${new Date(a.damagedDate).toLocaleDateString('en-GB')}) ` : '', a.damagedNote || '-'].join(''),
      W - M * 2 - 32
    )
    pdf.text(dmgLines, M + 33, y); y += dmgLines.length * 4.5 + 3

    if (a.writeOffReason) {
      pdf.setFont(PDF_FONT, 'bold'); pdf.text('Write-Off Reason:', M + 3, y); pdf.setFont(PDF_FONT, 'normal')
      const woLines = pdf.splitTextToSize(a.writeOffReason, W - M * 2 - 32)
      pdf.text(woLines, M + 33, y); y += woLines.length * 4.5 + 3
    }
    y += 3
  })

  y += 4

  // Signatures
  sectionHeader('Authorization')
  const sigW = (W - M * 2 - 8) / 3
  const sigH = 30
  const sigY = y
  const sigLabels = ['Prepared By\n(IT / Asset Dept)', 'Approved By\n(Department Manager)', 'Authorized By\n(Finance / Management)']
  for (let i = 0; i < 3; i++) {
    const sx = M + i * (sigW + 4)
    pdf.setDrawColor(150, 150, 150); pdf.rect(sx, sigY, sigW, sigH)
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(7.5)
    const [l1, l2] = sigLabels[i].split('\n')
    pdf.text(l1, sx + 2, sigY + 5)
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7)
    pdf.text(l2, sx + 2, sigY + 9)
    pdf.line(sx + 2, sigY + 21, sx + sigW - 2, sigY + 21)
    pdf.text('Signature', sx + 2, sigY + 25)
    pdf.line(sx + 2, sigY + 28, sx + sigW - 2, sigY + 28)
    pdf.text('Name / Date', sx + 2, sigY + 31)
  }
  y = sigY + sigH + 8

  // Footer
  pdf.setDrawColor(200, 200, 200); pdf.line(M, y, W - M, y); y += 4
  pdf.setFont(PDF_FONT, 'italic'); pdf.setFontSize(7); pdf.setTextColor(150, 150, 150)
  pdf.text(`Generated by AssetOS  •  ${ref}  •  ${today}`, W / 2, y + 3, { align: 'center' })

  openPdfInNewTab(pdf, `${ref}.pdf`)
}

async function generateRepairPDF(job: any, mode: 'repair' | 'writeoff', _logoB64?: string | null, _companyName?: string | null) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
  registerThaiFont(pdf)
  const W = 210, M = 15
  const today = new Date().toLocaleDateString('en-GB')
  const ref = `RPR-${String(job.id).padStart(5, '0')}`

  // ── Header — ใช้ helper กลางที่แก้แล้ว: โลโก้ auto-fit ไม่บิดเบี้ยว, หัวข้อ auto-fit ไม่ตกกรอบ ──
  let y = await drawHeader(
    pdf,
    mode === 'repair' ? 'EQUIPMENT GATE PASS' : 'EQUIPMENT WRITE-OFF',
    mode === 'repair' ? '(External Repair / Service)' : '(Asset Disposal Report)',
    ref, today, _logoB64, _companyName,
  )
  y -= 1  // เผื่อ spacing ให้ใกล้เคียงของเดิม (drawHeader เว้นบรรทัดมากกว่านิดหน่อย)

  // ── Equipment Details ────────────────────────────────────────────────────
  const sectionHeader = (title: string) => {
    pdf.setFillColor(226, 232, 240)
    pdf.rect(M, y, W - M * 2, 7, 'F')
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9)
    pdf.setTextColor(30, 41, 59)
    pdf.text(title, M + 3, y + 5)
    pdf.setTextColor(0, 0, 0)
    y += 10
  }

  const row = (label: string, value: string, col = M) => {
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
    pdf.text(label, col + 2, y)
    pdf.setFont(PDF_FONT, 'normal')
    pdf.text(value || '-', col + 42, y, { maxWidth: (W / 2) - 48 })
    y += 7
  }

  const row2 = (l1: string, v1: string, l2: string, v2: string) => {
    const cx = W / 2
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(8.5)
    pdf.text(l1, M + 2, y)
    pdf.setFont(PDF_FONT, 'normal')
    pdf.text(v1 || '-', M + 38, y)
    pdf.setFont(PDF_FONT, 'bold')
    pdf.text(l2, cx + 2, y)
    pdf.setFont(PDF_FONT, 'normal')
    pdf.text(v2 || '-', cx + 38, y)
    y += 7
  }

  sectionHeader('Equipment Information')
  row2('Asset Code:', job.assetCode, 'Asset Name:', job.assetName)
  row2('Serial Number:', job.serialNumber ?? '-', 'Department:', job.assetDept ?? '-')
  y += 2

  if (mode === 'repair') {
    sectionHeader('Service / Repair Details')
    row2('Vendor / Shop:', job.vendorName ?? '-', 'Ref Job:', ref)
    row2('Sent Date:', job.sentDate ? new Date(job.sentDate).toLocaleDateString('en-GB') : '-',
         'Expected Return:', job.expectedReturnDate ? new Date(job.expectedReturnDate).toLocaleDateString('en-GB') : '-')
    row('Estimated Cost:', job.estimatedCost ? `THB ${Number(job.estimatedCost).toLocaleString()} (Excl. VAT)` : '-')
    y += 2

    sectionHeader('Problem / Symptom Description')
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
    const lines = pdf.splitTextToSize(job.problem || '-', W - M * 2 - 8)
    pdf.text(lines, M + 3, y); y += lines.length * 5 + 6

    if (job.note) {
      pdf.setFont(PDF_FONT, 'italic'); pdf.setFontSize(8)
      pdf.text(`Note: ${job.note}`, M + 2, y); y += 8
    }

    // ── Terms ────────────────────────────────────────────────────────────
    pdf.setDrawColor(220, 220, 220); pdf.line(M, y, W - M, y); y += 6
    pdf.setFont(PDF_FONT, 'italic'); pdf.setFontSize(7.5); pdf.setTextColor(100, 100, 100)
    pdf.text('This Gate Pass authorizes the release of the above equipment for external repair/service only.', M + 2, y); y += 4.5
    pdf.text('Equipment must be returned by the expected return date. Unauthorized removal is prohibited.', M + 2, y)
    pdf.setTextColor(0, 0, 0)
    y += 10

    // ── Signature boxes ───────────────────────────────────────────────────
    sectionHeader('Authorized Signatures')
    const sigW = (W - M * 2 - 8) / 3
    const sigH = 34
    const sigY = y
    const sigCols = [M, M + sigW + 4, M + (sigW + 4) * 2]
    const sigLabels = ['Sender\n(Asset Manager / IT Dept)', 'Receiver\n(Vendor / Technician)', 'Approved By\n(Supervisor)']

    sigCols.forEach((sx, i) => {
      pdf.setDrawColor(150, 150, 150)
      pdf.rect(sx, sigY, sigW, sigH)
      pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(7.5)
      const labelLines = sigLabels[i].split('\n')
      pdf.text(labelLines[0], sx + 2, sigY + 5)
      pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7)
      pdf.text(labelLines[1], sx + 2, sigY + 9)
      pdf.line(sx + 2, sigY + 23, sx + sigW - 2, sigY + 23)
      pdf.text('Signature', sx + 2, sigY + 27)
      pdf.line(sx + 2, sigY + 30, sx + sigW - 2, sigY + 30)
      pdf.text('Name / Date', sx + 2, sigY + 33.5)
    })
    y = sigY + sigH + 8

  } else {
    // ── Write-Off PDF ─────────────────────────────────────────────────────
    sectionHeader('Reason / Justification for Write-Off')
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
    const reasonLines = pdf.splitTextToSize(job.writeOffReason || job.note || '-', W - M * 2 - 8)
    pdf.text(reasonLines, M + 3, y); y += reasonLines.length * 5 + 8

    sectionHeader('Condition at Time of Write-Off')
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
    pdf.text(`Status before write-off: ${job.type === 'EXTERNAL' ? 'Sent for External Repair' : 'In-House Repair'}`, M + 3, y); y += 6
    pdf.text(`Repair Job Ref: ${ref}`, M + 3, y); y += 6
    if (job.actualCost) {
      pdf.text(`Total Repair Cost Spent: THB ${Number(job.actualCost).toLocaleString()} (Excl. VAT)`, M + 3, y); y += 6
    }
    if (job.vendorName) {
      pdf.text(`Last Vendor / Repair Shop: ${job.vendorName}`, M + 3, y); y += 6
    }
    y += 4

    sectionHeader('Authorization & Approval')
    const sigW2 = (W - M * 2 - 8) / 3
    const sigH2 = 34
    const sigY2 = y
    const sigs2 = [
      'Requested By\n(Department Head)',
      'Verified By\n(Asset Manager / IT)',
      'Approved By\n(Management)',
    ]
    sigs2.forEach((label, i) => {
      const sx = M + i * (sigW2 + 4)
      pdf.setDrawColor(150, 150, 150)
      pdf.rect(sx, sigY2, sigW2, sigH2)
      pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(7.5)
      const lines2 = label.split('\n')
      pdf.text(lines2[0], sx + 2, sigY2 + 6)
      pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7)
      pdf.text(lines2[1], sx + 2, sigY2 + 10)
      pdf.line(sx + 2, sigY2 + 24, sx + sigW2 - 2, sigY2 + 24)
      pdf.text('Signature', sx + 2, sigY2 + 28)
      pdf.line(sx + 2, sigY2 + 31, sx + sigW2 - 2, sigY2 + 31)
      pdf.text('Name / Date', sx + 2, sigY2 + 34)
    })
    y = sigY2 + sigH2 + 8

    // Policy note
    pdf.setDrawColor(220, 220, 220); pdf.line(M, y, W - M, y); y += 5
    pdf.setFont(PDF_FONT, 'italic'); pdf.setFontSize(7.5); pdf.setTextColor(100, 100, 100)
    pdf.text('This document certifies that the above equipment has been assessed and approved for write-off disposal.', M + 2, y); y += 4.5
    pdf.text('The asset has been removed from the active inventory register effective the date of approval.', M + 2, y)
    pdf.setTextColor(0, 0, 0)
  }

  // ── Footer ────────────────────────────────────────────────────────────
  pdf.setDrawColor(200, 200, 200); pdf.line(M, y, W - M, y); y += 4
  pdf.setFont(PDF_FONT, 'italic'); pdf.setFontSize(7); pdf.setTextColor(150, 150, 150)
  pdf.text(`Generated by AssetOS  •  ${ref}  •  ${today}`, W / 2, y + 3, { align: 'center' })

  openPdfInNewTab(pdf, `${ref}.pdf`)
}

async function generateOnSitePDF(job: any, _logoB64?: string | null, _companyName?: string | null) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
  registerThaiFont(pdf)
  const W = 210, M = 15
  let y = M
  const today = new Date().toLocaleDateString('en-GB')
  const ref = `OSS-${String(job.id).padStart(5, '0')}`

  // Header — โลโก้ auto-fit (ไม่บิดเบี้ยว), หัวข้อ auto-fit (ไม่ตกกรอบ) — คงสีเขียวเทียลของรายงานนี้ไว้
  const LOGO_BOX_W = 55, LOGO_BOX_H = 24
  if (_logoB64) {
    try {
      const { w: natW, h: natH } = await getImageNaturalSize(_logoB64)
      const scale = Math.min(LOGO_BOX_W / natW, LOGO_BOX_H / natH)
      const drawW = natW * scale, drawH = natH * scale
      const ext = _logoB64.includes('image/png') ? 'PNG' : 'JPEG'
      pdf.addImage(_logoB64, ext, M, y + (LOGO_BOX_H - drawH) / 2, drawW, drawH, undefined, 'FAST')
    } catch (_) {}
  } else if (_companyName) { pdf.setFont(PDF_FONT,'bold'); pdf.setFontSize(12); pdf.setTextColor(15,23,42); pdf.text(_companyName, M, y+13); pdf.setTextColor(0,0,0) }
  pdf.setFillColor(13, 148, 136) // teal
  const hdrBoxX = W / 2, hdrBoxW = W / 2 - M, textMaxW = hdrBoxW - 6
  pdf.rect(hdrBoxX, y, hdrBoxW, 22, 'F')
  pdf.setTextColor(255, 255, 255)
  const titleSize = fitFontSize(pdf, 'ON SITE SERVICE REPORT', textMaxW, 11, 8, 'bold')
  pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(titleSize)
  pdf.text('ON SITE SERVICE REPORT', hdrBoxX + 3, y + 8)
  pdf.setFontSize(9); pdf.setFont(PDF_FONT, 'normal')
  pdf.text(`Ref: ${ref}`, hdrBoxX + 3, y + 15)
  pdf.text(`Date: ${today}`, hdrBoxX + 3, y + 20)
  y += 28

  // Asset info box
  pdf.setTextColor(0, 0, 0)
  pdf.setFillColor(248, 250, 252)
  pdf.rect(M, y, W - M * 2, 28, 'F')
  pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9)
  pdf.text('ASSET INFORMATION', M + 3, y + 6)
  pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
  pdf.text(`Asset Code : ${job.assetCode ?? '-'}`, M + 3, y + 13)
  pdf.text(`Asset Name : ${job.assetName ?? '-'}`, M + 3, y + 19)
  pdf.text(`Serial No. : ${job.serialNumber ?? '-'}`, W / 2, y + 13)
  pdf.text(`Status     : IN SERVICE`, W / 2, y + 19)
  y += 34

  // Technician info
  pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(10)
  pdf.setTextColor(13, 148, 136)
  pdf.text('SERVICE TECHNICIAN', M, y); y += 6
  pdf.setTextColor(0, 0, 0); pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
  const techRows = [
    ['Technician Name', job.technicianName ?? '-'],
    ['Contact / Phone', job.technicianContact ?? '-'],
    ['Vendor / Provider', job.vendorName ?? '-'],
    ['Contract / PO No.', job.serviceContractNo ?? '-'],
    ['Service Date', job.serviceDate ? new Date(job.serviceDate).toLocaleDateString('en-GB') : '-'],
    ['Service Location', job.serviceLocation ?? '-'],
  ]
  techRows.forEach(([label, val]) => {
    pdf.setFont(PDF_FONT, 'bold'); pdf.text(`${label}:`, M + 2, y)
    pdf.setFont(PDF_FONT, 'normal'); pdf.text(String(val), M + 50, y)
    y += 6
  })
  y += 4

  // Problem
  pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(10)
  pdf.setTextColor(13, 148, 136)
  pdf.text('PROBLEM / SYMPTOM', M, y); y += 6
  pdf.setTextColor(0, 0, 0); pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
  const problemLines = pdf.splitTextToSize(job.problem ?? '-', W - M * 2 - 4)
  pdf.text(problemLines, M + 2, y); y += problemLines.length * 5 + 6

  // Cost
  if (job.estimatedCost || job.actualCost) {
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(10)
    pdf.setTextColor(13, 148, 136)
    pdf.text('SERVICE COST', M, y); y += 6
    pdf.setTextColor(0, 0, 0); pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
    if (job.estimatedCost) { pdf.text(`Estimated Cost : ฿${Number(job.estimatedCost).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`, M + 2, y); y += 5 }
    if (job.actualCost)    { pdf.text(`Actual Cost    : ฿${Number(job.actualCost).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`, M + 2, y); y += 5 }
    y += 4
  }

  // Note
  if (job.note) {
    pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9); pdf.setTextColor(100, 100, 100)
    pdf.text('Note:', M, y); y += 5
    pdf.setFont(PDF_FONT, 'normal')
    const noteLines = pdf.splitTextToSize(job.note, W - M * 2 - 4)
    pdf.text(noteLines, M + 2, y); y += noteLines.length * 5 + 6
  }

  // Signature boxes
  y = Math.max(y, 210)
  pdf.setDrawColor(200, 200, 200)
  const boxW = (W - M * 2 - 10) / 2
  const sigBoxes = [
    { x: M,              label: 'Technician / Service Provider' },
    { x: M + boxW + 10,  label: 'Authorized / Received By' },
  ]
  sigBoxes.forEach(({ x, label }) => {
    pdf.rect(x, y, boxW, 28)
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8); pdf.setTextColor(120, 120, 120)
    pdf.text(label, x + boxW / 2, y + 24, { align: 'center' })
  })

  openPdfInNewTab(pdf, `${ref}.pdf`)
}


// ── Video URL helpers ─────────────────────────────────────────────────────
function getVideoEmbed(url: string): string | null {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`
  const gd = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/)
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`
  const vm = url.match(/vimeo\.com\/(\d+)/)
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`
  return null
}
function getVideoLabel(url: string) {
  if (url.includes('youtube') || url.includes('youtu.be')) return '▶️ YouTube'
  if (url.includes('drive.google')) return '📁 Google Drive'
  if (url.includes('vimeo')) return '🎬 Vimeo'
  return '🔗 Video'
}

// ── Repair Photo Strip ────────────────────────────────────────────────────
function RepairPhotoStrip({ images }: { images: string[] }) {
  const [viewUrl, setViewUrl] = useState<string | null>(null)
  if (!images || images.length === 0) return null
  return (
    <>
      <Group gap={4} wrap="wrap" mt={4}>
        {images.slice(0, 5).map((url, i) => (
          <Image key={i} src={url} w={36} h={36} radius="sm" fit="cover"
            style={{ cursor: 'pointer', border: '1px solid #e2e8f0' }}
            onClick={() => setViewUrl(url)} />
        ))}
        {images.length > 5 && <Text size="xs" c="dimmed">+{images.length - 5}</Text>}
      </Group>
      <Modal opened={!!viewUrl} onClose={() => setViewUrl(null)} size="xl" centered>
        <Image src={viewUrl ?? ''} fit="contain" mah={600} />
      </Modal>
    </>
  )
}

export default function MaintenancePage() {
  const { t, i18n } = useTranslation()
  const { companyLogoUrl, companyName } = useAuth()
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const [searchParams] = useSearchParams()

  // ── สถานะ/ประเภทงานซ่อม — ต้องอยู่ใน component ถึงจะเรียก t() ได้ ──
  const getStatusLabel = (status: string) => ({
    OPEN: `🔧 ${t('maintenance.statusOpen', 'In Repair')}`,
    COMPLETED: `✅ ${t('maintenance.statusCompleted', 'Completed')}`,
    REPLACED: `🔄 ${t('maintenance.statusReplaced', 'Replaced')}`,
    WRITTEN_OFF: `⚠️ ${t('maintenance.statusWrittenOff', 'Reported Damaged')}`,
  } as Record<string, string>)[status] ?? status
  const getTypeLabel = (type: string) => ({
    EXTERNAL: t('repair.typeExternalBadge', 'External'),
    INTERNAL: t('repair.typeInternalBadge', 'Internal'),
    ON_SITE: t('repair.typeOnSiteBadge', 'On Site'),
  } as Record<string, string>)[type] ?? type
  const [jobs, setJobs] = useState<any[]>([])
  const [repairAssets, setRepairAssets] = useState<any[]>([])
  const [writeOffAssets, setWriteOffAssets] = useState<any[]>([])
  const [selectedWoIds, setSelectedWoIds] = useState<Set<number>>(new Set())
  const [vendors, setVendors] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  // ถ้ามี ?assetCode= ใน URL (เช่นคลิกมาจาก Inventory History) ให้เติม search อัตโนมัติ
  const [search, setSearch] = useState(() => searchParams.get('assetCode') ?? '')
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('assetCode') ? 'ALL' : 'OPEN')
  // ── ตัวกรองชั้นที่ 2 (ภายในสถานะที่เลือกอยู่แล้ว) — ประเภทงานซ่อม (Internal/External/On Site)
  // และหมวดหมู่ครุภัณฑ์ (มาจาก job.asset.category ที่ join มาให้อยู่แล้วตอน findAll()) ──
  const [typeFilter, setTypeFilter] = useState<string>('ALL')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [jobType, setJobType] = useState('EXTERNAL')
  // Internal repair sub-type
  const [internalRepairType, setInternalRepairType] = useState<string>('GENERAL')
  // Consumable usage
  const [consumableItems, setConsumableItems] = useState<any[]>([])
  const [consumableUsed, setConsumableUsed] = useState<{ id: number; name: string; qty: number }[]>([])
  // Asset used for repair
  const [allAssets, setAllAssets] = useState<any[]>([])
  const [assetUsedId, setAssetUsedId] = useState<string | null>(null)
  const [form, setForm] = useState({
    problem: '', vendorName: '', pickupDate: '', sentDate: '', expectedReturnDate: '', estimatedCost: 0, note: '',
    // On Site fields
    technicianName: '', technicianContact: '', serviceDate: '', serviceLocation: '', serviceContractNo: '',
  })
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [activeJob, setActiveJob] = useState<any>(null)
  const [editingJob, setEditingJob] = useState<any>(null)
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [qvAsset, setQvAsset] = useState<any>(null)
  const [qvOpen, setQvOpen] = useState(false)
  const [editForm, setEditForm] = useState({ type:'EXTERNAL', problem:'', vendorName:'', pickupDate:'', sentDate:'', expectedReturnDate:'', estimatedCost:0, note:'', technicianName:'', technicianContact:'', serviceDate:'', serviceLocation:'', serviceContractNo:'', videoUrls:[] as string[] })
  const [editVideoInput, setEditVideoInput] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  // Internal Repair — แก้ไข consumable ที่เบิกใช้ (แยก state จาก Create modal เพื่อไม่ให้ปนกัน)
  const [editInternalRepairType, setEditInternalRepairType] = useState<string>('GENERAL')
  const [editConsumableUsed, setEditConsumableUsed] = useState<{ id: number; name: string; qty: number }[]>([])
  const [uploadingVideoId, setUploadingVideoId] = useState<number | null>(null)
  const [markingAvailableId, setMarkingAvailableId] = useState<number | null>(null)
  const [completeOpen, { open: openComplete, close: closeComplete }] = useDisclosure(false)
  const [completeForm, setCompleteForm] = useState({ actualReturnDate: '', actualCost: 0, resolution: '', note: '' })
  const [replaceOpen, { open: openReplace, close: closeReplace }] = useDisclosure(false)
  const [replaceForm, setReplaceForm] = useState({ newAssetCode: '', newSerialNumber: '', newAssetName: '', actualCost: 0, note: '' })
  const [swapOpen, { open: openSwap, close: closeSwap }] = useDisclosure(false)
  const [woOpen, { open: openWO, close: closeWO }] = useDisclosure(false)
  const [woForm, setWoForm] = useState({ reason: '', note: '' })
  const [woImages, setWoImages] = useState<File[]>([])

  const load = async () => {
    setLoading(true)
    try {
      const [j, a, v, cons, assetAll] = await Promise.all([
        getRepairJobs(), getRepairAssets(), getVendors(),
        getInventoryItems(), getAllAssetsAdmin(),
      ])
      setJobs(j.data)
      const allFetched = Array.isArray(a.data) ? a.data : []
      const repairList = allFetched.filter((x: any) => x.status === 'REPAIR')
      const openJobAssetIds = new Set((j.data as any[]).filter(job => job.status === 'OPEN').map((job: any) => job.assetId))
      setRepairAssets(repairList.filter((x: any) => !openJobAssetIds.has(x.id)))
      setVendors((v.data ?? []).filter((x: any) => x.isActive !== false))
      setConsumableItems((cons.data ?? []).filter((x: any) => x.isActive !== false && (x.stockQty ?? x.currentStock ?? 0) > 0))
      const allAssetList = Array.isArray(assetAll.data) ? assetAll.data : []
      setAllAssets(allAssetList.filter((x: any) => x.status === 'AVAILABLE'))
      setWriteOffAssets(allAssetList.filter((x: any) => x.status === 'DAMAGED'))
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const addImages = (files: File[]) => {
    setImageFiles(p => [...p, ...files])
    files.forEach(f => { const r = new FileReader(); r.onload = e => setImagePreviews(p => [...p, e.target?.result as string]); r.readAsDataURL(f) })
  }

  // ── รูปภาพตอน Edit — แยกจากตอนสร้างงานใหม่ เพราะต้องจัดการ "รูปเดิมที่มีอยู่แล้ว" (ลบออกได้)
  // ร่วมกับ "รูปใหม่ที่เพิ่งเลือก" (ยังไม่ได้ upload จนกว่าจะกด Save) พร้อมกัน — editExistingImages
  // เก็บ URL ของรูปเดิมที่ "ยังจะเก็บไว้" (ลบออกจาก array นี้เมื่อกดลบ ไม่ใช่ลบออกจาก DB ทันที
  // จนกว่าจะกด Save) ส่วน editNewImageFiles/editNewImagePreviews คือรูปใหม่ที่เพิ่งเลือกเพิ่ม ──
  const [editExistingImages, setEditExistingImages] = useState<string[]>([])
  const [editNewImageFiles, setEditNewImageFiles] = useState<File[]>([])
  const [editNewImagePreviews, setEditNewImagePreviews] = useState<string[]>([])
  const addEditImages = (files: File[]) => {
    setEditNewImageFiles(p => [...p, ...files])
    files.forEach(f => { const r = new FileReader(); r.onload = e => setEditNewImagePreviews(p => [...p, e.target?.result as string]); r.readAsDataURL(f) })
  }

  const handleCreate = async () => {
    if (!selectedAssetId) { notifications.show({ color: 'red', message: 'Please select an Asset' }); return }
    if (!form.problem) { notifications.show({ color: 'red', message: 'Please describe the problem' }); return }
    setSaving(true)
    try {
      // Build note with internal repair details
      let noteExtra = form.note
      if (jobType === 'INTERNAL') {
        const parts = [`Sub-type: ${internalRepairType}`]
        if (internalRepairType === 'USE_ASSET' && assetUsedId) {
          const a = allAssets.find((x: any) => String(x.id) === assetUsedId)
          parts.push(`Used Asset: ${a?.assetCode ?? assetUsedId}`)
        }
        if (internalRepairType === 'USE_CONSUMABLE' && consumableUsed.length > 0) {
          parts.push(`Consumables: ${consumableUsed.map(c => `${c.name} x${c.qty}`).join(', ')}`)
        }
        noteExtra = parts.join(' | ') + (form.note ? ` | ${form.note}` : '')
      }

      const res = await createRepairJob({
        assetId: parseInt(selectedAssetId),
        type: jobType,
        problem: form.problem,
        ...(form.vendorName ? { vendorName: form.vendorName } : {}),
        ...(form.pickupDate ? { pickupDate: form.pickupDate } : {}),
        ...(form.sentDate ? { sentDate: form.sentDate } : {}),
        ...(form.expectedReturnDate ? { expectedReturnDate: form.expectedReturnDate } : {}),
        ...(form.estimatedCost ? { estimatedCost: form.estimatedCost } : {}),
        ...(noteExtra ? { note: noteExtra } : {}),
      })

      // Upload images
      if (imageFiles.length > 0) {
        const fd = new FormData(); imageFiles.forEach(f => fd.append('files', f))
        await uploadRepairImages(res.data.id, fd)
      }

      // Deduct consumable stock
      if (jobType === 'INTERNAL' && internalRepairType === 'USE_CONSUMABLE' && consumableUsed.length > 0) {
        await Promise.all(consumableUsed.map(c =>
          adjustStock(c.id, { change: -c.qty, note: `Used in repair job #${res.data.id}` })
        ))
      }

      notifications.show({ color: 'teal', message: 'Repair job created successfully' })
      closeCreate()
      setImageFiles([]); setImagePreviews([])
      setConsumableUsed([]); setAssetUsedId(null)
      setInternalRepairType('GENERAL')
      load()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
    finally { setSaving(false) }
  }

  const handleComplete = async () => {
    if (!activeJob) return
    try {
      await completeRepair(activeJob.id, { ...completeForm, actualCost: completeForm.actualCost || undefined })
      notifications.show({ color: 'teal', message: 'Repair completed & asset returned to service' }); closeComplete(); load()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
  }

  const handleReplace = async () => {
    if (!activeJob) return
    if (!replaceForm.newAssetCode) { notifications.show({ color: 'red', message: 'Please select a replacement asset' }); return }
    try {
      await replaceAsset(activeJob.id, { ...replaceForm, actualCost: replaceForm.actualCost || undefined })
      notifications.show({ color: 'violet', message: 'Asset replaced successfully. Old asset marked as Write-Off.' }); closeReplace(); load()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
  }

  const handleWriteOff = async () => {
    if (!activeJob) return
    if (!woForm.reason) { notifications.show({ color: 'red', message: 'Please provide a write-off reason' }); return }
    try {
      await writeOffRepair(activeJob.id, woForm)
      if (woImages.length > 0) { const fd = new FormData(); woImages.forEach(f => fd.append('files', f)); await uploadRepairImages(activeJob.id, fd) }
      notifications.show({ color: 'red', message: 'Asset written off and removed from service' }); closeWO(); load()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
  }

  const filtered = jobs.filter(j => {
    const matchStatus = statusFilter === 'ALL' || j.status === statusFilter
    const matchType = typeFilter === 'ALL' || j.type === typeFilter
    const matchCategory = !categoryFilter || j.asset?.category === categoryFilter
    const q = search.toLowerCase()
    const matchQ = !q || j.assetCode?.toLowerCase().includes(q) || j.assetName?.toLowerCase().includes(q) || j.vendorName?.toLowerCase().includes(q) || j.serialNumber?.toLowerCase().includes(q) || j.problem?.toLowerCase().includes(q)
    return matchStatus && matchType && matchCategory && matchQ
  })

  // ── ตัวเลือกหมวดหมู่สำหรับ dropdown — ดึงจาก category จริงที่ปรากฏอยู่ในงานซ่อมทั้งหมด (ไม่ใช่
  // ทุก category ในระบบ) กันไม่ให้มีตัวเลือกที่กดแล้วไม่มีผลลัพธ์อะไรเลยโผล่ในลิสต์ ──
  const categoryOptions = Array.from(new Set(jobs.map(j => j.asset?.category).filter(Boolean))).sort()

  const openCount = jobs.filter(j => j.status === 'OPEN').length

  return (
    <Box p={isMobile ? 12 : 24}>
      <Group justify="space-between" mb="xl">
        <Box>
          <Group gap={8}>
            <Text style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Inter,sans-serif', color: '#0f172a' }}>{t('maintenance.title','Repair')}</Text>
            {jobs.filter(j => j.status === 'OPEN').length > 0 && (
              <Badge color="orange" size="lg" circle>{jobs.filter(j => j.status === 'OPEN').length}</Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">{t('maintenance.subtitle','Manage repair jobs, track equipment in service, and write-off reports')}</Text>
        </Box>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={isSmall ? 2 : 2} mb="xl" style={{ maxWidth: 680 }}>
        {[
          { label: t('maintenance.inProgress','In Repair'), count: jobs.filter(j=>j.status==='OPEN').length, color: '#f97316', status: 'OPEN' },
          { label: t('maintenance.completed','Completed'), count: jobs.filter(j=>j.status==='COMPLETED').length, color: '#14b8a6', status: 'COMPLETED' },
        ].map(s => (
          <Paper key={s.label} withBorder radius="lg" p="md" onClick={() => setStatusFilter(s.status)}
            style={{ borderLeft: `4px solid ${s.color}`, cursor: 'pointer', background: statusFilter === s.status ? '#f8fafc' : '#fff' }}>
            <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</Text>
            <Text size="xl" fw={800} style={{ color: s.color }}>{s.count}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Filters */}
      <Stack gap="sm" mb="md">
        <Group gap="sm" wrap="wrap">
          <TextInput placeholder={t("maintenance.searchPlaceholder","Search asset code, name, vendor, problem...")}
            leftSection={<IconSearch size={14} />} value={search} onChange={e => setSearch(e.target.value)} style={{ width: isMobile ? '100%' : 280 }} />
          <Button size="xs" radius="lg" variant={statusFilter === 'ALL' ? 'filled' : 'light'} color="gray" onClick={() => setStatusFilter('ALL')}>{t("maintenance.allJobs","All Jobs")}</Button>
          <Select placeholder={t("maintenance.allCategories","All categories")} clearable searchable size="xs"
            value={categoryFilter} onChange={setCategoryFilter}
            data={categoryOptions.map(c => ({ value: c, label: c }))}
            style={{ width: isMobile ? '100%' : 180 }} />
        </Group>
        {/* ── ตัวกรองชั้นที่ 2: ประเภทงานซ่อม — กรองซ้อนกับสถานะที่เลือกไว้ด้านบนเสมอ (เช่น
        "กำลังซ่อม" + "ส่งซ่อมนอก" พร้อมกันได้) ไม่ใช่แท็บแยกที่ล้างสถานะทิ้ง ── */}
        <Group gap={6}>
          {[
            { value: 'ALL', label: t('maintenance.allTypes', 'All') },
            { value: 'INTERNAL', label: `🔧 ${t('repairModal.typeInternal', 'ซ่อมภายใน')}` },
            { value: 'EXTERNAL', label: `🏢 ${t('repairModal.typeExternal', 'ส่งซ่อมนอก')}` },
            { value: 'ON_SITE', label: `🛠️ ${t('repairModal.typeOnSite', 'ช่างมาที่หน้างาน')}` },
          ].map(opt => (
            <Button key={opt.value} size="xs" radius="lg" variant={typeFilter === opt.value ? 'filled' : 'subtle'}
              color={typeFilter === opt.value ? 'teal' : 'gray'} onClick={() => setTypeFilter(opt.value)}>
              {opt.label}
            </Button>
          ))}
        </Group>
      </Stack>

      {/* Job List */}
      {/* Damaged Assets Section */}
      {statusFilter === 'WRITTEN_OFF' && (
        <Box mb="lg">
          {writeOffAssets.length === 0 ? (
            <Center p="xl"><Stack align="center" gap={4}><IconX size={40} color="#cbd5e1" /><Text c="dimmed" size="sm">t('maintenance.noDamaged','No damaged assets found')</Text></Stack></Center>
          ) : (
            <>
              <Group justify="space-between" mb="sm">
                <Group gap={8}>
                  <Text size="sm" fw={600} c="dimmed">{writeOffAssets.length} damaged asset(s)</Text>
                  {selectedWoIds.size > 0 && (
                    <Badge color="red" variant="light">{selectedWoIds.size} selected</Badge>
                  )}
                </Group>
                <Group gap={8}>
                  {selectedWoIds.size > 0 && (
                    <>
                      <Button size="xs" variant="subtle" color="gray"
                        onClick={() => setSelectedWoIds(new Set())}>{t('repair.clear')}</Button>
                      <Button size="xs" color="red" leftSection={<IconFileText size={13} />}
                        onClick={async () => await generateAssetWriteOffPDF(writeOffAssets.filter(a => selectedWoIds.has(a.id)), await (companyLogoUrl ? urlToBase64(companyLogoUrl) : Promise.resolve(null)), companyName)}>
                        Print Write-Off Report ({selectedWoIds.size})
                      </Button>
                    </>
                  )}
                  <Button size="xs" variant="light" color="red" leftSection={<IconFileText size={13} />}
                    onClick={async () => generateAssetWriteOffPDF(writeOffAssets, await (companyLogoUrl ? urlToBase64(companyLogoUrl) : Promise.resolve(null)), companyName)}>
                    Print All ({writeOffAssets.length})
                  </Button>
                </Group>
              </Group>
              <Stack gap="xs">
                {writeOffAssets.map(a => (
                  <Paper key={a.id} withBorder radius="lg" p="sm"
                    style={{ borderLeft: '4px solid #ef4444', cursor: 'pointer', background: selectedWoIds.has(a.id) ? '#fef2f2' : '#fff' }}
                    onClick={() => setSelectedWoIds(prev => {
                      const next = new Set(prev)
                      next.has(a.id) ? next.delete(a.id) : next.add(a.id)
                      return next
                    })}>
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap={8}>
                        <Box style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid #ef4444', background: selectedWoIds.has(a.id) ? '#ef4444' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {selectedWoIds.has(a.id) && <IconCheck size={12} color="white" />}
                        </Box>
                        <Box>
                          <Group gap={6}>
                            <Text fw={700} size="sm" style={{ fontFamily: 'Inter,sans-serif' }}>{a.assetCode}</Text>
                            <Badge color="red" variant="light" size="xs">{t("common.damaged","Damaged")}</Badge>
                          </Group>
                          <Text size="xs" c="dimmed">{a.name}</Text>
                          <Group gap={10} mt={2}>
                            {a.serialNumber && <Text size="xs" c="dimmed">S/N: {a.serialNumber}</Text>}
                            {a.departmentCode && <Text size="xs" c="dimmed">🏢 {a.departmentCode}</Text>}
                            {a.category && <Text size="xs" c="dimmed">📁 {a.category}</Text>}
                          </Group>
                        </Box>
                      </Group>
                      <ActionIcon color="red" variant="light" size="sm"
                        onClick={async e => { e.stopPropagation(); await generateAssetWriteOffPDF([a], await (companyLogoUrl ? urlToBase64(companyLogoUrl) : Promise.resolve(null)), companyName) }}>
                        <IconFileText size={14} />
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}

      {loading ? <Center p="xl"><Loader color="orange" /></Center> : statusFilter === 'WRITTEN_OFF' ? null : filtered.length === 0 ? (
        <Center p="xl"><Stack align="center" gap={4}><IconTool size={40} color="#cbd5e1" /><Text c="dimmed" size="sm">{t("maintenance.noJobs","No repair jobs found")}</Text></Stack></Center>
      ) : (
        <Stack gap="sm">
          {filtered.map(job => {
            const bc = job.status==='OPEN'?'#f97316':job.status==='COMPLETED'?'#14b8a6':job.status==='REPLACED'?'#8b5cf6':'#ef4444'
            const overdue = job.status === 'OPEN' && job.expectedReturnDate && new Date(job.expectedReturnDate) < new Date()
            return (
              <Card key={job.id} withBorder radius="lg" p="md" style={{ borderLeft: `4px solid ${bc}`, background: overdue ? '#fff7ed' : '#fff' }}>
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text fw={600} size="sm" style={{ color: '#0d9488', cursor: 'pointer', textDecoration: 'underline dotted' }}
                      onClick={() => scanLookupAsset(job.assetCode).then(r => { if (r.data?.found) { setQvAsset(r.data); setQvOpen(true) } })}>
                      {job.assetCode}
                    </Text>
                    <Text size="xs" c="dimmed">{job.assetName}{job.serialNumber ? ` · S/N: ${job.serialNumber}` : ''}</Text>
                  </Box>
                  <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Badge color={STATUS_COLOR[job.status]} variant="light" size="sm">{getStatusLabel(job.status)}</Badge>
                {(() => {
                  // ── รวมปุ่มจัดการทั้งหมด (สูงสุด 9 ปุ่ม) เป็น array ข้อมูลชุดเดียว — desktop
                  // ใช้แถวไอคอน+tooltip เดิม (ไม่แตะ), มือถือใช้เมนู ⋮ ที่มี label กำกับทุกปุ่ม
                  // (tooltip ใช้ไม่ได้จริงบนจอสัมผัส เหมือนปัญหาที่แก้ไปแล้วใน AssetQuickView) ──
                  const jobActions = [
                    ...(job.status === 'OPEN' ? [
                      { key: 'edit', label: t("repair.editJobDetails","Edit job details"), color: 'blue', icon: (s: number) => <IconEdit size={s} />,
                        onClick: () => {
                          setEditingJob(job)
                          setEditForm({ type: job.type??'EXTERNAL', problem: job.problem??'', vendorName: job.vendorName??'', pickupDate: job.pickupDate?.slice(0,10)??'', sentDate: job.sentDate?.slice(0,10)??'', expectedReturnDate: job.expectedReturnDate?.slice(0,10)??'', estimatedCost: job.estimatedCost??0, note: job.note??'', technicianName: job.technicianName??'', technicianContact: job.technicianContact??'', serviceDate: job.serviceDate?.slice(0,10)??'', serviceLocation: job.serviceLocation??'', serviceContractNo: job.serviceContractNo??'', videoUrls: job.videoUrls??[] })
                          setEditVideoInput('')
                          setEditExistingImages(job.images ?? [])
                          setEditNewImageFiles([])
                          setEditNewImagePreviews([])
                          const existing = job.consumablesUsed ?? []
                          setEditConsumableUsed(existing)
                          setEditInternalRepairType(existing.length > 0 ? 'USE_CONSUMABLE' : 'GENERAL')
                          openEdit()
                        } },
                      { key: 'complete', label: t("repair.markRepaired","Mark as Repaired / Return"), color: 'teal', icon: (s: number) => <IconCheck size={s} />,
                        onClick: () => { setActiveJob(job); setCompleteForm({ actualReturnDate: new Date().toISOString().slice(0,10), actualCost: 0, resolution: '', note: '' }); openComplete() } },
                      { key: 'swap', label: t("repair.swapWithSpare","Replacement"), color: 'violet', icon: (s: number) => <IconArrowsExchange size={s} />,
                        onClick: () => { setActiveJob(job); openSwap() } },
                      { key: 'damaged', label: t('maintenance.markDamaged', 'Report Damaged'), color: 'red', icon: (s: number) => <IconX size={s} />,
                        onClick: () => { setActiveJob(job); setWoForm({ reason:'', note:'' }); setWoImages([]); openWO() } },
                      { key: 'available', label: t('maintenance.markAvailableCancelRepair', 'Mark Asset as AVAILABLE (cancel repair)'), color: 'cyan', icon: (s: number) => <IconRotateClockwise2 size={s} />,
                        onClick: async () => {
                          if (!confirm(t('maintenance.confirmMarkAvailable', 'Mark {{code}} as AVAILABLE and close this repair job?', { code: job.assetCode }))) return
                          setMarkingAvailableId(job.assetId)
                          try {
                            await updateAsset(job.assetId, { status: 'AVAILABLE' })
                            notifications.show({ color: 'teal', message: `${job.assetCode} → AVAILABLE` })
                            load()
                          } catch { notifications.show({ color: 'red', message: t('maintenance.failedUpdateStatus', 'Failed to update status') }) }
                          finally { setMarkingAvailableId(null) }
                        } },
                    ] : []),
                    { key: 'printMain', label: job.type === 'INTERNAL' ? t('repair.printInternal', 'Print Internal Repair Record') : job.type === 'ON_SITE' ? t('maintenance.printOnSiteReport', 'Print On Site Service Report') : t('repair.printOutward', 'Print Outward Gate Pass'),
                      color: 'gray', icon: (s: number) => <IconFileText size={s} />,
                      onClick: async () => {
                        if (job.type === 'ON_SITE') {
                          const lb = companyLogoUrl ? await urlToBase64(companyLogoUrl) : null
                          generateOnSitePDF(job, lb, companyName ?? null)
                        } else {
                          try {
                            const res = await downloadRepairPdf(job.id, job.type === 'INTERNAL' ? 'internal' : 'outward', undefined, i18n.language === 'th' ? 'th' : 'en')
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                            window.open(url, '_blank')
                            setTimeout(() => URL.revokeObjectURL(url), 30_000)
                          } catch { notifications.show({ color: 'red', message: t('maintenance.pdfGenerationFailed', 'Failed to generate PDF') }) }
                        }
                      } },
                    ...(job.type === 'EXTERNAL' && job.status === 'COMPLETED' ? [
                      { key: 'printInward', label: t('repair.printInward', 'Print Return Receipt'), color: 'teal', icon: (s: number) => <IconFileCheck size={s} />,
                        onClick: async () => {
                          try {
                            const res = await downloadRepairPdf(job.id, 'inward', undefined, i18n.language === 'th' ? 'th' : 'en')
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                            window.open(url, '_blank')
                            setTimeout(() => URL.revokeObjectURL(url), 30_000)
                          } catch { notifications.show({ color: 'red', message: t('maintenance.pdfGenerationFailed', 'Failed to generate PDF') }) }
                        } },
                    ] : []),
                    ...(job.videoUrls?.length > 0 ? [
                      { key: 'video', label: t('maintenance.viewVideoCount', 'View Video ({{count}})', { count: job.videoUrls.length }), color: 'teal', icon: (s: number) => <IconVideo size={s} />,
                        onClick: () => window.open(job.videoUrls[0], '_blank') },
                    ] : []),
                    ...((job.status === 'WRITTEN_OFF' || job.status === 'REPLACED') ? [
                      { key: 'printWriteOff', label: t("repair.printWriteOff","Print Write-Off Report"), color: 'red', icon: (s: number) => <IconFileText size={s} />,
                        onClick: async () => { const lb = companyLogoUrl ? await urlToBase64(companyLogoUrl) : null; await generateRepairPDF(job, 'writeoff', lb, companyName ?? null) } },
                    ] : []),
                  ]

                  if (isMobile) {
                    return (
                      <Menu shadow="md" width={230} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" size="lg" style={{ flexShrink: 0 }}><IconDotsVertical size={18} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {jobActions.map(a => (
                            <Menu.Item key={a.key} leftSection={a.icon(15)} color={a.color} onClick={a.onClick}>
                              {a.label}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    )
                  }

                  return (
                    <Group gap={4} style={{ flexShrink: 0 }} wrap="nowrap">
                      {jobActions.map(a => (
                        <Tooltip key={a.key} label={a.label}>
                          <ActionIcon color={a.color} variant="light"
                            loading={a.key === 'available' && markingAvailableId === job.assetId}
                            onClick={a.onClick}>
                            {a.icon(16)}
                          </ActionIcon>
                        </Tooltip>
                      ))}
                    </Group>
                  )
                })()}
                </Group>
                </Group>

                <Divider my={8} />

                <Group gap={6} mb={6} wrap="wrap">
                  <Badge color={job.type==='EXTERNAL'?'blue':job.type==='ON_SITE'?'teal':'gray'} variant="outline" size="xs">{getTypeLabel(job.type)}</Badge>
                  {overdue && <Badge color="red" variant="filled" size="xs">⚠ {t('maintenance.overdueBadge', 'OVERDUE')}</Badge>}
                </Group>
                <Group gap={12} wrap="wrap">
                  {job.vendorName && <Text size="xs" c="dimmed">🏢 {job.vendorName}</Text>}
                  {job.sentDate && <Text size="xs" c="dimmed">📅 {t("maintenance.sentLabel","Sent")}: {new Date(job.sentDate).toLocaleDateString('en-GB')}</Text>}
                  {job.expectedReturnDate && <Text size="xs" c={overdue ? 'red' : 'dimmed'}>⏰ Due: {new Date(job.expectedReturnDate).toLocaleDateString('en-GB')}</Text>}
                  {job.estimatedCost ? <Text size="xs" c="dimmed">💰 THB {Number(job.estimatedCost).toLocaleString()}</Text> : null}
                </Group>
                {job.problem && <Text size="xs" c="dimmed" mt={4} lineClamp={1}>🔍 {job.problem}</Text>}
                {job.type === 'INTERNAL' && Array.isArray(job.consumablesUsed) && job.consumablesUsed.length > 0 && (
                  <Group gap={4} mt={4} wrap="wrap">
                    <Text size="xs" c="dimmed">📦</Text>
                    {job.consumablesUsed.map((c: any, ci: number) => (
                      <Badge key={ci} size="xs" color="grape" variant="light">{c.name} × {c.qty}</Badge>
                    ))}
                  </Group>
                )}
                {job.videoUrls?.length > 0 && (
                  <Group gap={6} mt={4} wrap="wrap">
                    {job.videoUrls.map((url: string, i: number) => (
                      <Anchor key={i} href={url} target="_blank" size="xs" c="teal">
                        {getVideoLabel(url)} #{i+1}
                      </Anchor>
                    ))}
                  </Group>
                )}
                <RepairPhotoStrip images={job.images ?? []} />
                {job.replacedByAssetCode && (
                  <Alert icon={<IconArrowsExchange size={12} />} color="violet" variant="light" mt={6} p={6}>
                    <Text size="xs">Replaced by: <b>{job.replacedByAssetCode}</b> · S/N: {job.replacedBySerialNumber}</Text>
                  </Alert>
                )}
                {job.writeOffReason && <Text size="xs" c="red" mt={4} lineClamp={1}>📋 {job.writeOffReason}</Text>}
              </Card>
            )
          })}
        </Stack>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <Modal opened={editOpen} onClose={closeEdit} size="md" radius="lg"
          title={<Text fw={600}>✏️ Edit Repair Job — {editingJob.assetCode}</Text>}>
          <Stack gap="sm">
            <Select label={t("maintenance.repairType","ประเภทการซ่อม")}
              value={editForm.type}
              onChange={v => setEditForm(p => ({ ...p, type: v ?? 'INTERNAL' }))}
              data={[
                { value: 'EXTERNAL', label: '🏢 ' + t('repairModal.typeExternal', 'ส่งซ่อมนอก (ส่งให้ผู้ขาย/ร้านซ่อม)') },
                { value: 'INTERNAL', label: '🔧 ' + t('repairModal.typeInternal', 'ซ่อมภายใน') },
                { value: 'ON_SITE',  label: '🛠️ ' + t('repairModal.typeOnSite', 'ช่างมาที่หน้างาน') },
              ]} />
            <Textarea label="Problem / Symptom *" minRows={3}
              value={editForm.problem} onChange={e => setEditForm(p => ({ ...p, problem: e.target.value }))} />
            {editForm.type === 'INTERNAL' && (
              <Box style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
                <Text size="sm" fw={600} mb={8}>{t('repair.internalMethod')}</Text>
                <Select value={editInternalRepairType}
                  onChange={v => { setEditInternalRepairType(v ?? 'GENERAL'); if (v !== 'USE_CONSUMABLE') setEditConsumableUsed([]) }}
                  data={[
                    { value: 'GENERAL',        label: '🔧 General Repair (no parts used)' },
                    { value: 'USE_CONSUMABLE', label: '📦 Use Consumable / Spare Parts' },
                  ]}
                  mb="sm" />
                {editInternalRepairType === 'USE_CONSUMABLE' && (() => {
                  // รวม item ที่เคยถูกเลือกไว้แล้ว (แม้สต็อกปัจจุบันจะเป็น 0 เพราะถูกใช้ที่อื่นไปแล้ว)
                  // เข้ากับ list หลัก เพื่อไม่ให้ dropdown แสดงค่าว่างทั้งที่จริงๆมีการเลือกไว้อยู่
                  const missingFromList = editConsumableUsed
                    .filter(cu => cu.id && !consumableItems.some((c: any) => c.id === cu.id))
                    .map(cu => ({ id: cu.id, name: cu.name, stockQty: 0, unit: 'PCS' }))
                  const editConsumableOptions = [...consumableItems, ...missingFromList]

                  return (
                    <Box>
                      <Text size="xs" c="dimmed" mb={6}>Select consumable items used in this repair (stock will be deducted automatically)</Text>
                      {editConsumableUsed.map((cu, i) => {
                        const selectedItem = editConsumableOptions.find((c: any) => c.id === cu.id)
                        return (
                          <Box key={i} mb={8} p={10} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <Group gap="sm" align="flex-end">
                              <Select style={{ flex: 1 }} size="xs"
                                placeholder={t("maintenance.consumableSelectPlaceholder","Select consumable...")}
                                searchable
                                data={editConsumableOptions.map((c: any) => ({
                                  value: String(c.id),
                                  label: `${c.name}${c.brand ? ` — ${c.brand}` : ''} (${c.stockQty ?? c.currentStock ?? 0} ${c.unit ?? 'PCS'})`,
                                }))}
                                value={cu.id ? String(cu.id) : null}
                                onChange={v => {
                                  const item = editConsumableOptions.find((c: any) => String(c.id) === v)
                                  if (item) setEditConsumableUsed(p => p.map((x, j) => j === i ? { id: item.id, name: item.name, qty: x.qty } : x))
                                }}
                              />
                              <NumberInput size="xs" style={{ width: 90 }} label={t("common.qty","Qty")} min={1}
                                value={cu.qty}
                                onChange={v => setEditConsumableUsed(p => p.map((x, j) => j === i ? { ...x, qty: Number(v)||1 } : x))} />
                              <ActionIcon color="red" variant="light" size="sm" mb={1}
                                onClick={() => setEditConsumableUsed(p => p.filter((_, j) => j !== i))}>
                                <IconX size={12} />
                              </ActionIcon>
                            </Group>
                            {selectedItem && (selectedItem.stockQty ?? 0) === 0 && (
                              <Text size="xs" c="orange" mt={4}>⚠ Current stock is 0 — confirm this quantity is still correct</Text>
                            )}
                          </Box>
                        )
                      })}
                      <Button size="xs" variant="light" color="teal" leftSection={<IconPlus size={12} />}
                        onClick={() => setEditConsumableUsed(p => [...p, { id: 0, name: '', qty: 1 }])}>
                        {t("maintenance.addConsumable","Add Consumable")}
                      </Button>
                    </Box>
                  )
                })()}
              </Box>
            )}
            {editForm.type === 'EXTERNAL' && (<>
              <Select label={t('repair.vendorLabel')} searchable clearable
                placeholder={t("maintenance.vendorSelectPlaceholder","Select or type vendor...")}
                data={vendors.map((v: any) => ({ value: v.name, label: localizedName(v, i18n.language) }))}
                value={editForm.vendorName || null}
                onChange={v => setEditForm(p => ({ ...p, vendorName: v ?? '' }))}
                onSearchChange={q => { if (q) setEditForm(p => ({ ...p, vendorName: q })) }} />
              <SmartDateInput label={t("maintenance.pickupDate","Scheduled Pickup Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={editForm.pickupDate} onChange={v => setEditForm(p => ({ ...p, pickupDate: v }))} clearable />
              <SmartDateInput label={t("maintenance.sentDate","Sent Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={editForm.sentDate} onChange={v => setEditForm(p => ({ ...p, sentDate: v }))} clearable />
              <SmartDateInput label={t("maintenance.expectedReturnDate","Expected Return Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                value={editForm.expectedReturnDate} onChange={v => setEditForm(p => ({ ...p, expectedReturnDate: v }))} clearable />
              <NumberInput label="Estimated Cost (THB)" min={0}
                value={editForm.estimatedCost} onChange={v => setEditForm(p => ({ ...p, estimatedCost: Number(v) }))} />
            </>)}
            {editForm.type === 'ON_SITE' && (
              <Box p={12} style={{ background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                <Text size="sm" fw={600} c="teal.7" mb={10}>{t("maintenance.onSiteDetails","On Site Service Details")}</Text>
                <Stack gap="sm">
                  <SimpleGrid cols={2}>
                    <TextInput label={t('maintenance.technicianName')} placeholder={t("maintenance.technicianNamePlaceholder","Technician name")}
                      value={editForm.technicianName} onChange={e => setEditForm(p => ({ ...p, technicianName: e.target.value }))} />
                    <TextInput label={t("maintenance.contactPhone","Contact / Phone")}
                      value={editForm.technicianContact} onChange={e => setEditForm(p => ({ ...p, technicianContact: e.target.value }))} />
                  </SimpleGrid>
                  <SimpleGrid cols={2}>
                    <SmartDateInput label="Service Date *" placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                      value={editForm.serviceDate} onChange={v => setEditForm(p => ({ ...p, serviceDate: v }))} clearable />
                    <TextInput label={t('repair.serviceLocation')}
                      value={editForm.serviceLocation} onChange={e => setEditForm(p => ({ ...p, serviceLocation: e.target.value }))} />
                  </SimpleGrid>
                  <SimpleGrid cols={2}>
                    <TextInput label={t("maintenance.contractPo","Contract / PO No.")}
                      value={editForm.serviceContractNo} onChange={e => setEditForm(p => ({ ...p, serviceContractNo: e.target.value }))} />
                    <NumberInput label={t('maintenance.estimatedCost')} min={0} decimalScale={2}
                      value={editForm.estimatedCost} onChange={v => setEditForm(p => ({ ...p, estimatedCost: Number(v) || 0 }))} />
                  </SimpleGrid>
                  <Select label={t("maintenance.vendorServiceProvider","Vendor / Service Provider")} searchable clearable
                    placeholder={t("maintenance.vendorSelectPlaceholder","Select or type vendor...")}
                    data={vendors.map((v: any) => ({ value: v.name, label: localizedName(v, i18n.language) }))}
                    value={editForm.vendorName || null}
                    onChange={v => setEditForm(p => ({ ...p, vendorName: v ?? '' }))} />
                </Stack>
              </Box>
            )}
            <Textarea label={t('repair.note')} minRows={2}
              value={editForm.note} onChange={e => setEditForm(p => ({ ...p, note: e.target.value }))} />

            {/* ── Photos — เพิ่ม/ลบรูปได้ตอน Edit เหมือนกับตอนสร้างงานใหม่ (เดิมแก้ไขได้แค่วิดีโอ
            อย่างเดียว ไม่มีช่องจัดการรูปเลย) รูปเดิมลบออกได้ (ยืนยันจริงตอนกด Save) รูปใหม่เพิ่มได้
            ไม่จำกัด ── */}
            <Box>
              <Text size="sm" fw={500} mb={6}>{t("maintenance.attachPhotos","Attach Equipment Photos")}</Text>
              <Group gap="sm" wrap="wrap">
                {editExistingImages.map((src, i) => (
                  <Box key={`existing-${i}`} style={{ position: 'relative' }}>
                    <Image src={src} w={72} h={72} radius="md" fit="cover" style={{ border: '1px solid #e2e8f0' }} />
                    <ActionIcon size="xs" color="red" variant="filled" radius="xl" style={{ position: 'absolute', top: -4, right: -4 }}
                      onClick={() => setEditExistingImages(p => p.filter((_, j) => j !== i))}>
                      <IconX size={10} />
                    </ActionIcon>
                  </Box>
                ))}
                {editNewImagePreviews.map((src, i) => (
                  <Box key={`new-${i}`} style={{ position: 'relative' }}>
                    <Image src={src} w={72} h={72} radius="md" fit="cover" style={{ border: '2px solid #14b8a6' }} />
                    <ActionIcon size="xs" color="red" variant="filled" radius="xl" style={{ position: 'absolute', top: -4, right: -4 }}
                      onClick={() => { setEditNewImageFiles(p => p.filter((_, j) => j !== i)); setEditNewImagePreviews(p => p.filter((_, j) => j !== i)) }}>
                      <IconX size={10} />
                    </ActionIcon>
                  </Box>
                ))}
                <FileButton onChange={fs => addEditImages(fs)} accept="image/*" multiple>
                  {(props) => <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconUpload size={12} />}>{t('repair.addPhotos')}</Button>}
                </FileButton>
              </Group>
            </Box>

            {/* ── Video Links ── */}
            <Box>
              <Group gap={8} mb={6}>
                <TextInput
                  placeholder={t("maintenance.videoUrlPlaceholder","YouTube / Google Drive / Vimeo URL...")}
                  value={editVideoInput}
                  onChange={e => setEditVideoInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editVideoInput.trim()) {
                      e.preventDefault()
                      setEditForm(p => ({ ...p, videoUrls: [...(p.videoUrls||[]), editVideoInput.trim()] }))
                      setEditVideoInput('')
                    }
                  }}
                  style={{ flex: 1 }} size="sm"
                />
                <Button size="sm" variant="light" color="teal"
                  disabled={!editVideoInput.trim()}
                  onClick={() => {
                    setEditForm(p => ({ ...p, videoUrls: [...(p.videoUrls||[]), editVideoInput.trim()] }))
                    setEditVideoInput('')
                  }}>{t("common.add","Add")}</Button>
              </Group>
              {(editForm.videoUrls||[]).map((url, i) => (
                <Group key={i} gap={6} p={6} style={{ background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{getVideoLabel(url)}</Text>
                  <Text size="xs" c="teal" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {url}
                  </Text>
                  <ActionIcon size="xs" variant="subtle" color="blue" component="a" href={url} target="_blank">
                    <IconVideo size={11} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="subtle" color="red"
                    onClick={() => setEditForm(p => ({ ...p, videoUrls: (p.videoUrls||[]).filter((_,idx)=>idx!==i) }))}>
                    <IconX size={11} />
                  </ActionIcon>
                </Group>
              ))}
            </Box>

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeEdit}>{t("common.cancel","Cancel")}</Button>
              <Button color="blue" loading={savingEdit} onClick={async () => {
                if (!editForm.problem.trim()) { notifications.show({ color: 'red', message: 'Problem description required' }); return }
                if (editForm.type === 'INTERNAL' && editInternalRepairType === 'USE_CONSUMABLE') {
                  if (editConsumableUsed.length === 0) {
                    notifications.show({ color: 'red', message: 'Please add at least one consumable item' }); return
                  }
                  if (editConsumableUsed.some(c => !c.id)) {
                    notifications.show({ color: 'red', message: 'Please select a consumable item for every row' }); return
                  }
                }
                setSavingEdit(true)
                try {
                  const consumablesUsed = editForm.type === 'INTERNAL' && editInternalRepairType === 'USE_CONSUMABLE'
                    ? editConsumableUsed
                    : [] // ไม่ใช้ consumable แล้ว (หรือเปลี่ยน type ออกจาก INTERNAL) — ส่ง [] เพื่อให้ backend คืนสต็อกเดิมที่เคยตัดไป
                  await updateRepairJob(editingJob.id, { ...editForm, videoUrls: editForm.videoUrls, consumablesUsed })

                  // ── รูปภาพ — ลบรูปเดิมที่ถูกกดลบออก (เทียบกับ images เดิมของ job ตอนเปิด Edit)
                  // แล้วค่อย upload รูปใหม่ที่เพิ่งเลือกเพิ่ม ทำแยกจาก updateRepairJob() ข้างบน เพราะ
                  // เป็นคนละ endpoint (multipart สำหรับรูป, JSON ธรรมดาสำหรับข้อมูลอื่น) ──
                  const originalImages: string[] = editingJob.images ?? []
                  const removedImages = originalImages.filter(url => !editExistingImages.includes(url))
                  await Promise.all(removedImages.map(url => deleteRepairImage(editingJob.id, url)))
                  if (editNewImageFiles.length > 0) {
                    const fd = new FormData(); editNewImageFiles.forEach(f => fd.append('files', f))
                    await uploadRepairImages(editingJob.id, fd)
                  }

                  notifications.show({ color: 'teal', message: 'Repair job updated' })
                  closeEdit(); load()
                } catch (e: any) {
                  notifications.show({ color: 'red', message: e?.response?.data?.message ?? 'Update failed' })
                }
                finally { setSavingEdit(false) }
              }}>{t('repair.saveChanges')}</Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Create Modal */}
      <Modal opened={createOpen} onClose={closeCreate} size="lg" radius="lg"
        title={<Text fw={700} style={{ fontFamily: 'Inter,sans-serif', fontSize: 16 }}>🔧 New Repair Job</Text>}>
        <Stack gap="sm">
          <Select label="Asset (status: REPAIR) *" searchable clearable placeholder={t("maintenance.assetSelectPlaceholder","Select asset...")}
            data={repairAssets.map((a: any) => ({ value: String(a.id), label: `${a.assetCode} — ${a.name} (S/N: ${a.serialNumber ?? '-'})` }))}
            value={selectedAssetId} onChange={setSelectedAssetId}
            description={
              repairAssets.length === 0
                ? '⚠ No available assets — all REPAIR assets already have open repair jobs, or no assets in REPAIR status.'
                : `${repairAssets.length} asset(s) available (assets with open jobs are excluded)`
            } />
          <Select label={t("maintenance.repairType","Repair Type")} value={jobType}
            onChange={v => { setJobType(v ?? 'EXTERNAL'); setInternalRepairType('GENERAL'); setConsumableUsed([]); setAssetUsedId(null) }}
            data={[
              { value: 'EXTERNAL', label: '🏢 External Repair (Send to Vendor)' },
              { value: 'INTERNAL', label: '🔧 Internal Repair' },
              { value: 'ON_SITE',  label: '🛠️ On Site Service' },
            ]} />
          <Textarea label="Problem / Symptom *" placeholder={t("maintenance.issueDescPlaceholder","Describe the issue in detail...")} minRows={3} value={form.problem} onChange={e => setForm(p => ({ ...p, problem: e.target.value }))} />

          {/* ── On Site Service fields ── */}
          {jobType === 'ON_SITE' && (
            <Box p={12} style={{ background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
              <Text size="sm" fw={600} c="teal.7" mb={10}>{t("maintenance.onSiteDetails","On Site Service Details")}</Text>
              <Stack gap="sm">
                <SimpleGrid cols={2}>
                  <TextInput label={t('maintenance.technicianName')} placeholder={t('maintenance.technicianPlaceholder')}
                    value={form.technicianName} onChange={e => setForm(p => ({ ...p, technicianName: e.target.value }))} />
                  <TextInput label={t("maintenance.contactPhone","Contact / Phone")} placeholder={t('maintenance.contactPlaceholder')}
                    value={form.technicianContact} onChange={e => setForm(p => ({ ...p, technicianContact: e.target.value }))} />
                </SimpleGrid>
                <SimpleGrid cols={2}>
                  <SmartDateInput label="Service Date *" placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
                    value={form.serviceDate} onChange={v => setForm(p => ({ ...p, serviceDate: v }))} clearable />
                  <TextInput label={t('repair.serviceLocation')} placeholder={t('maintenance.serviceLocationPlaceholder')}
                    value={form.serviceLocation} onChange={e => setForm(p => ({ ...p, serviceLocation: e.target.value }))} />
                </SimpleGrid>
                <SimpleGrid cols={2}>
                  <TextInput label={t("maintenance.contractPo","Contract / PO No.")} placeholder={t('maintenance.contractPlaceholder')}
                    value={form.serviceContractNo} onChange={e => setForm(p => ({ ...p, serviceContractNo: e.target.value }))} />
                  <NumberInput label={t('maintenance.estimatedCost')} min={0} decimalScale={2}
                    value={form.estimatedCost} onChange={v => setForm(p => ({ ...p, estimatedCost: Number(v) || 0 }))} />
                </SimpleGrid>
                <Select label={t("maintenance.vendorServiceProvider","Vendor / Service Provider")} searchable clearable placeholder={t("maintenance.vendorSelectPlaceholder","Select or type vendor...")}
                  data={vendors.map(v => ({ value: v.name, label: localizedName(v, i18n.language) }))}
                  value={form.vendorName || null}
                  onChange={v => setForm(p => ({ ...p, vendorName: v ?? '' }))} />
              </Stack>
            </Box>
          )}

          {/* External fields */}
          {jobType === 'EXTERNAL' && (<>
            <Select label={t('repair.vendorLabel')} searchable clearable placeholder={t("maintenance.vendorSelectPlaceholder","Select or type vendor...")}
              data={vendors.map((v: any) => ({ value: v.name, label: localizedName(v, i18n.language) }))}
              value={form.vendorName || null}
              onChange={v => setForm(p => ({ ...p, vendorName: v ?? '' }))}
              onSearchChange={q => { if (q) setForm(p => ({ ...p, vendorName: q })) }} />
            <Grid>
              <Grid.Col span={4}>
                <SmartDateInput label={t("maintenance.pickupDate","Scheduled Pickup Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={form.pickupDate} onChange={v => setForm(p => ({ ...p, pickupDate: v }))} clearable />
              </Grid.Col>
              <Grid.Col span={4}>
                <SmartDateInput label={t("maintenance.sentDate","Sent Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={form.sentDate} onChange={v => setForm(p => ({ ...p, sentDate: v }))} clearable />
              </Grid.Col>
              <Grid.Col span={4}>
                <SmartDateInput label={t("maintenance.expectedReturnDate","Expected Return Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={form.expectedReturnDate} onChange={v => setForm(p => ({ ...p, expectedReturnDate: v }))} clearable />
              </Grid.Col>
            </Grid>
            <NumberInput label="Estimated Cost (THB, Excl. VAT)" min={0} decimalScale={2} value={form.estimatedCost} onChange={v => setForm(p => ({ ...p, estimatedCost: Number(v)||0 }))} />
          </>)}

          {/* Internal Repair sub-type */}
          {jobType === 'INTERNAL' && (
            <Box style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <Text size="sm" fw={600} mb={8}>{t('repair.internalMethod')}</Text>
              <Select value={internalRepairType}
                onChange={v => { setInternalRepairType(v ?? 'GENERAL'); setConsumableUsed([]); setAssetUsedId(null) }}
                data={[
                  { value: 'GENERAL',        label: '🔧 General Repair (no parts used)' },
                  { value: 'USE_CONSUMABLE',  label: '📦 Use Consumable / Spare Parts' },
                  { value: 'USE_ASSET',       label: '💻 Use Another Asset as Replacement Part' },
                ]}
                mb="sm"
              />

              {/* Consumable picker */}
              {internalRepairType === 'USE_CONSUMABLE' && (
                <Box>
                  <Text size="xs" c="dimmed" mb={6}>Select consumable items used in this repair (stock will be deducted automatically)</Text>
                  {consumableUsed.map((cu, i) => {
                    const selectedItem = consumableItems.find((c: any) => c.id === cu.id)
                    return (
                    <Box key={i} mb={8} p={10} style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <Group gap="sm" align="flex-end" mb={selectedItem ? 6 : 0}>
                        <Select style={{ flex: 1 }} size="xs"
                          placeholder={t("maintenance.consumableSelectPlaceholder","Select consumable...")}
                          searchable
                          data={consumableItems.map((c: any) => ({
                            value: String(c.id),
                            label: `${c.name}${c.brand ? ` — ${c.brand}` : ''} (${c.stockQty ?? c.currentStock ?? 0} ${c.unit ?? 'PCS'})`,
                          }))}
                          value={cu.id ? String(cu.id) : null}
                          onChange={v => {
                            const item = consumableItems.find((c: any) => String(c.id) === v)
                            if (item) setConsumableUsed(p => p.map((x, j) => j === i ? { id: item.id, name: item.name, qty: x.qty } : x))
                          }}
                        />
                        <NumberInput size="xs" style={{ width: 90 }} label={t("common.qty","Qty")} min={1}
                          max={selectedItem ? (selectedItem.stockQty ?? selectedItem.currentStock ?? 999) : 999}
                          value={cu.qty}
                          onChange={v => setConsumableUsed(p => p.map((x, j) => j === i ? { ...x, qty: Number(v)||1 } : x))} />
                        <ActionIcon color="red" variant="light" size="sm" mb={1}
                          onClick={() => setConsumableUsed(p => p.filter((_, j) => j !== i))}>
                          <IconX size={12} />
                        </ActionIcon>
                      </Group>
                      {selectedItem && (
                        <Group gap={6}>
                          <Text size="xs" c="dimmed">SKU: <b>{selectedItem.sku}</b></Text>
                          {selectedItem.location && <Text size="xs" c="dimmed">· {selectedItem.location}</Text>}
                          <Text size="xs" c={cu.qty > (selectedItem.stockQty ?? 0) ? 'red' : 'teal'} fw={500}>
                            · Stock: {selectedItem.stockQty ?? 0} {selectedItem.unit ?? 'PCS'}
                          </Text>
                        </Group>
                      )}
                    </Box>
                    )
                  })}
                  <Button size="xs" variant="light" color="teal" leftSection={<IconPlus size={12} />}
                    onClick={() => setConsumableUsed(p => [...p, { id: 0, name: '', qty: 1 }])}>
                    {t("maintenance.addConsumable","Add Consumable")}
                  </Button>
                </Box>
              )}

              {/* Asset used */}
              {internalRepairType === 'USE_ASSET' && (
                <Select
                  label={t("maintenance.assetReplacementPart","Asset used as replacement part")}
                  placeholder={t("maintenance.availableAssetPlaceholder","Select available asset...")}
                  searchable clearable
                  data={allAssets.map((a: any) => ({ value: String(a.id), label: `${a.assetCode} — ${a.name} (S/N: ${a.serialNumber ?? '-'})` }))}
                  value={assetUsedId}
                  onChange={setAssetUsedId}
                  description="This asset's code will be recorded in the repair log"
                />
              )}
            </Box>
          )}
          <TextInput label={t('repair.note')} placeholder={t("maintenance.additionalNotesPlaceholder","Additional notes...")} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
          <Box>
            <Text size="sm" fw={500} mb={6}>{t("maintenance.attachPhotos","Attach Equipment Photos")}</Text>
            <Group gap="sm" wrap="wrap">
              {imagePreviews.map((src, i) => (
                <Box key={i} style={{ position: 'relative' }}>
                  <Image src={src} w={72} h={72} radius="md" fit="cover" style={{ border: '1px solid #e2e8f0' }} />
                  <ActionIcon size="xs" color="red" variant="filled" radius="xl" style={{ position: 'absolute', top: -4, right: -4 }}
                    onClick={() => { setImageFiles(p=>p.filter((_,j)=>j!==i)); setImagePreviews(p=>p.filter((_,j)=>j!==i)) }}>
                    <IconX size={10} />
                  </ActionIcon>
                </Box>
              ))}
              <FileButton onChange={fs => addImages(fs)} accept="image/*" multiple>
                {(props) => <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconUpload size={12} />}>{t('repair.addPhotos')}</Button>}
              </FileButton>
            </Group>
          </Box>
          <Group justify="flex-end" pt="xs" style={{ borderTop: '1px solid #f1f5f9' }}>
            <Button variant="default" onClick={closeCreate}>{t("common.cancel","Cancel")}</Button>
            <Button loading={saving} onClick={handleCreate} style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>{t("repair.createRepairJob","Create Repair Job")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Complete Modal */}
      <Modal opened={completeOpen} onClose={closeComplete} size="sm" radius="lg"
        title={<Text fw={700} style={{ fontFamily: 'Inter,sans-serif' }}>✅ Mark as Repaired & Returned</Text>}>
        <Stack gap="sm">
          {activeJob && <Paper withBorder p="sm" radius="md" style={{ background: '#f0fdf4' }}><Text fw={600} size="sm">{activeJob.assetCode} — {activeJob.assetName}</Text><Text size="xs" c="dimmed">S/N: {activeJob.serialNumber}</Text></Paper>}
          <SmartDateInput label={t("maintenance.actualReturnDate","Actual Return Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")} value={completeForm.actualReturnDate} onChange={v => setCompleteForm(p => ({ ...p, actualReturnDate: v }))} clearable />
          <NumberInput label="Actual Repair Cost (THB, Excl. VAT)" min={0} decimalScale={2} value={completeForm.actualCost} onChange={v => setCompleteForm(p => ({ ...p, actualCost: Number(v)||0 }))} />
          <Textarea label={t("maintenance.resolutionLabel","Work Done / Resolution")} placeholder={t("maintenance.resolutionPlaceholder","Describe what was repaired...")} minRows={2} value={completeForm.resolution} onChange={e => setCompleteForm(p => ({ ...p, resolution: e.target.value }))} />
          <TextInput label={t('repair.note')} value={completeForm.note} onChange={e => setCompleteForm(p => ({ ...p, note: e.target.value }))} />
          <Group justify="flex-end"><Button variant="default" onClick={closeComplete}>{t("common.cancel","Cancel")}</Button><Button onClick={handleComplete} color="teal">{t('repair.confirmReturn')}</Button></Group>
        </Stack>
      </Modal>

      <SwapWithSpareModal
        opened={swapOpen}
        onClose={closeSwap}
        jobId={activeJob?.id ?? null}
        originalAssetId={activeJob?.assetId ?? null}
        originalAssetCode={activeJob?.assetCode}
        originalSerialNumber={activeJob?.serialNumber}
        originalCategory={allAssets.find((a: any) => a.assetCode === activeJob?.assetCode)?.category ?? activeJob?.category ?? null}
        originalLocation={activeJob?.location}
        onSuccess={load}
      />

      {/* Write-Off Modal */}
      <Modal opened={woOpen} onClose={closeWO} size="sm" radius="lg"
        title={<Text fw={700} style={{ fontFamily: 'Inter,sans-serif' }}>❌ {t('maintenance.damagedTitle', 'Cannot Repair — Mark as Damaged')}</Text>}>
        <Stack gap="sm">
          {activeJob && (
            <Alert icon={<IconAlertTriangle size={14} />} color="orange" variant="light">
              <Text size="sm">{t('maintenance.damagedBody', 'Asset {{code}} — {{name}} will be changed to status DAMAGED', { code: activeJob.assetCode, name: activeJob.assetName })}</Text>
              <Text size="xs" c="dimmed" mt={4}>⚠️ หลังจากนี้ต้องไปที่หน้า <b>{t('repair.writeOff')}</b> เพื่อออกรายงานขออนุมัติตัดออกจากบัญชีทรัพย์สิน</Text>
            </Alert>
          )}
          <Textarea label={t('maintenance.damageReasonLabel')} placeholder={t('maintenance.damageReasonPlaceholder', 'e.g. Beyond economic repair, physical damage, component unavailable...')} minRows={3} value={woForm.reason} onChange={e => setWoForm(p => ({ ...p, reason: e.target.value }))} />
          <TextInput label={t('repair.note')} value={woForm.note} onChange={e => setWoForm(p => ({ ...p, note: e.target.value }))} />
          <FileButton onChange={fs => setWoImages(p => [...p, ...fs])} accept="image/*" multiple>
            {(props) => <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconUpload size={12} />}>Attach Condition Photos {woImages.length > 0 && `(${woImages.length})`}</Button>}
          </FileButton>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeWO}>{t("common.cancel","Cancel")}</Button>
            <Button onClick={handleWriteOff} color="orange">Confirm — Mark as Damaged</Button>
          </Group>
        </Stack>
      </Modal>

      <AssetQuickView
        asset={qvAsset}
        opened={qvOpen}
        onClose={() => setQvOpen(false)}
      />
    </Box>
  )
}
